import { all, takeEvery, put, call } from 'redux-saga/effects';
import groupsActions from './actions';
import { rsf, db } from '@iso/lib/firebase/firebase';
import { cloneGroup } from '@iso/lib/remod-helpers/globalHelpers';
import { getCurrentUserEmail } from '@iso/lib/firebase/firebase.authentication.util';
import { saveState } from '@iso/lib/helpers/localStorage';
import { splitArrayIntoMultipleArraysForSagaQuery } from '@iso/lib/remod-helpers/globalHelpers';

function* loadMyGroupsSaga() {
    let myGroups = {};
    try {
        const loggedInUserEmail = yield call(getCurrentUserEmail);
        const userQuerySnapshot = yield call(
            rsf.firestore.getCollection, db.collection('users').where('email', '==', loggedInUserEmail)
        );
        let userDetails;
        if (!userQuerySnapshot.empty) {
            userDetails = userQuerySnapshot.docs[0].data();
        } else {
            throw new Error('User with specified email not found');
        }
        if (userDetails.userGroups.length === 0) {
            yield put(
                groupsActions.createLoadMyGroupsActionSuccess({
                    remodelumGroups: {},
                })
            );
            return;
        }
        const collectionReference = db
            .collection('groups')
            .where('name', 'in', userDetails.userGroups);
        const groupsSnapshot = yield call(rsf.firestore.getCollection, collectionReference);
        const groupMap = new Map(
            groupsSnapshot.docs.map(doc => [doc.data().name, []])
        );
        const groupKeys = Array.from(groupMap.keys());
        for (let i = 0; i < groupKeys.length; i += 10) {
            const batch = groupKeys.slice(i, i + 10);
            
            const usersRef = db.collection('users')
                .where('userGroups', 'array-contains-any', batch);
            const usersSnapshot = yield call(rsf.firestore.getCollection, usersRef);

            usersSnapshot.forEach((userDoc) => {
                const userData = userDoc.data();
                userData.userGroups.forEach(groupName => {
                    if (groupMap.has(groupName) && loggedInUserEmail !== userData.email) {
                        groupMap.get(groupName).push({
                            key: userData.uid,
                            name: userData.firstname + ' ' + userData.lastname,
                            email: userData.email
                        });
                    }
                });
            });
        }
        groupsSnapshot.forEach((group) => {
            myGroups[group.id] = {
                ...group.data(),
                assignedUsers: groupMap.get(group.data().name).map(x => ({...x, createdBy: group.data().createdBy, groupName: group.data().name}))
            };
        });
        yield put(
            groupsActions.createLoadMyGroupsActionSuccess({
                remodelumGroups: myGroups,
            })
        );
    } catch (error) {
        yield put(
            groupsActions.createLoadMyGroupsActionFailed({
                groupsError: error
            })
        );
    }
}

function* getGroupByName(payload) {
    let groupResult = undefined;
    const collectionReference = db
        .collection('groups')
        .where('name', '==', payload.name);
    const groupsSnapshot = yield call(rsf.firestore.getCollection, collectionReference);
    groupsSnapshot.forEach((group) => {
        groupResult = { ...group.data(), key: group.id };
    });
    return groupResult;
}

function* getAssignedUsersToGroup(payload) {
    let usersResult = {};
    const collectionReference = db
        .collection('users')
        .where('userGroups', 'array-contains', payload.name);
    const usersSnapshot = yield call(rsf.firestore.getCollection, collectionReference);
    usersSnapshot.forEach((group) => {
        usersResult[group.id] = { ...group.data() };
    });
    return usersResult;
}

function* createGroupSaga({ payload }) {
    try {
        const existingGroup = yield call(getGroupByName, { name: payload.name });
        if (existingGroup) {
            yield put(
                groupsActions.createCreateGroupActionFailed({
                    groupsError: 'Group given name already exists'
                })
            );
            yield put(
                groupsActions.createCreateGroupActionFailed({
                    groupsError: ''
                })
            );
            return;
        }
        const loggedInUserEmail = yield call(getCurrentUserEmail);
        payload.createdBy = loggedInUserEmail;
        payload.lastModifiedBy = loggedInUserEmail;
        const group = cloneGroup(payload);
        const id = db.collection('groups').doc().id;
        yield call(rsf.firestore.setDocument, 'groups/' + id, group);
        const userQuerySnapshot = yield call(
            rsf.firestore.getCollection, db.collection('users').where('email', '==', loggedInUserEmail)
        );
        let userDetails, userId;
        if (!userQuerySnapshot.empty) {
            userDetails = userQuerySnapshot.docs[0].data();
            userId = userQuerySnapshot.docs[0].id;
        } else {
            throw new Error('User with specified email not found');
        }
        userDetails.userGroups.push(group.name);
        yield call(rsf.firestore.setDocument, 'users/' + userId, userDetails);
        saveState('user', ({ ...userDetails }));
        yield put(
            groupsActions.createCreateGroupActionSuccess({
                groupsSuccess: '',
            })
        );
        yield put(
            groupsActions.createLoadMyGroupsActionStart()
        );
    } catch (error) {
        yield put(
            groupsActions.createCreateGroupActionFailed({
                groupsError: error,
            })
        );
    }
}

function* inviteMemberSaga({payload}) {
    try {
        const collectionReference = db
            .collection('users')
            .where('email', '==', payload.email);
        const usersSnapshot = yield call(rsf.firestore.getCollection, collectionReference);
        const batch = db.batch();
        usersSnapshot.forEach((user) => {
            const updatedGroups = user.data().userGroups;
            if (updatedGroups.includes(payload.name)) {
                return;
            }
            updatedGroups.push(payload.name);
            const userRef = db.collection('users').doc(user.id);
            batch.update(userRef, {
                userGroups: updatedGroups
            });
        });
        yield call([batch, batch.commit]);
        yield put(
            groupsActions.createInviteMemberActionSuccess({
                referralsSuccess: '',
            })
        );
        yield put(
            groupsActions.createLoadMyGroupsActionStart()
        );
    }catch(error) {
        yield put(
            groupsActions.createInviteMemberActionFailed({
                groupsError: error,
            })
        );
    }
}

function* editGroupSaga({ payload }) {
    try {
        const existingGroup = yield call(getGroupByName, { name: payload.name });
        if (existingGroup) {
            yield put(
                groupsActions.createEditGroupActionFailed({
                    groupsError: 'Group given name already exists'
                })
            );
            yield put(
                groupsActions.createEditGroupActionFailed({
                    groupsError: ''
                })
            );
            return;
        }
        const loggedInUserEmail = yield call(getCurrentUserEmail);
        payload.createdBy = payload.createdBy ?? loggedInUserEmail;
        payload.lastModifiedBy = loggedInUserEmail;
        payload.lastModifiedAt = undefined;
        const id = payload.key;
        const group = cloneGroup(payload);
        yield call(rsf.firestore.setDocument, 'groups/' + id, group);
        const userQuerySnapshot = yield call(
            rsf.firestore.getCollection, db.collection('users').where('email', '==', loggedInUserEmail)
        );
        let userDetails;
        if (!userQuerySnapshot.empty) {
            userDetails = userQuerySnapshot.docs[0].data();
        } else {
            throw new Error('User with specified email not found');
        }
        const collectionReference = db
            .collection('users')
            .where('userGroups', 'array-contains', payload.previous);
        const usersSnapshot = yield call(rsf.firestore.getCollection, collectionReference);
        const batch = db.batch();
        usersSnapshot.forEach((user) => {
            const userData = user.data();
            const userRef = db.collection('users').doc(user.id);
            batch.update(userRef, {
                userGroups: prepareGroups(userData, group.name, payload.previous)
            });
        });
        yield call([batch, batch.commit]);
        delete userDetails['userGroups'];
        saveState('user', ({ ...userDetails }));
        yield put(
            groupsActions.createEditGroupActionSuccess({
                referralsSuccess: '',
            })
        );
        yield put(
            groupsActions.createLoadMyGroupsActionStart()
        );
    } catch (error) {
        yield put(
            groupsActions.createEditGroupActionFailed({
                groupsError: error,
            })
        );
    }
}

function prepareGroups (userRef, newName, previousName) {
    const userGroups = userRef.userGroups.filter(x => x !== previousName);
    userGroups.push(newName);
    return userGroups;
}

function* unassignMembersSaga({ payload }) {
    try {
        if (payload.length === 0) {
            return;
        }
        const groupName = payload[0].name;
        const batch = db.batch();
        let multipleArrays = splitArrayIntoMultipleArraysForSagaQuery(payload.map(x => x.email));
        for (let i = 0; i < multipleArrays.length; i++) {
            
            const usersCollectionReference = db
                .collection('users')
                .where('email', 'in', multipleArrays[i]);
            const usersSnapshot = yield call(
                rsf.firestore.getCollection,
                usersCollectionReference
            );
            usersSnapshot.docs.forEach((user) => {
                const userData = user.data();
                const userRef = db.collection('users').doc(user.id);
                batch.update(userRef, {
                    userGroups: userData.userGroups.filter(x => x !== groupName)
                });
            });
            
        }
        yield call([batch, batch.commit]);
        yield put(
            groupsActions.createDeleteGroupActionSuccess({
                groupsSuccess: '',
            })
        );
        yield put(
            groupsActions.createLoadMyGroupsActionStart()
        );
    }catch(error) {
        yield put(
            groupsActions.createDeleteGroupActionFailed({
                groupsError: error,
            })
        );
    }
    
}

function* abandonGroupSaga({ payload }) {

    try {
        const loggedInUserEmail = yield call(getCurrentUserEmail);
        const userQuerySnapshot = yield call(
            rsf.firestore.getCollection, db.collection('users').where('email', '==', loggedInUserEmail)
        );
        let userDetails, userId;
        if (!userQuerySnapshot.empty) {
            userDetails = userQuerySnapshot.docs[0].data();
            userId = userQuerySnapshot.docs[0].id;
        } else {
            throw new Error('User with specified email not found');
        }
        userDetails.userGroups = userDetails.userGroups.filter(x => x !== payload.name);
        yield call(rsf.firestore.setDocument, 'users/' + userId, userDetails);
        delete userDetails['userGroups'];
        saveState('user', ({ ...userDetails }));
        yield put(
            groupsActions.createDeleteGroupActionSuccess({
                groupsSuccess: '',
            })
        );
        yield put(
            groupsActions.createLoadMyGroupsActionStart()
        );
    } catch (error) {
        yield put(
            groupsActions.createDeleteGroupActionFailed({
                groupsError: error,
            })
        );
    }
}

function* deleteGroupSaga({ payload }) {
    const id = payload.groupId;
    try {
        const snapshot = yield call(rsf.firestore.getDocument, 'groups/' + id);
        const group = snapshot.data();
        const usersAssigned = yield call(getAssignedUsersToGroup, { name: group.name });
        if (Object.keys(usersAssigned).length > 1) {
            yield put(
                groupsActions.createDeleteGroupActionFailed({
                    groupsError: 'Cannot delete as group has assigned members'
                })
            );
            yield put(
                groupsActions.createDeleteGroupActionFailed({
                    groupsError: ''
                })
            );
            return;
        }
        yield call(rsf.firestore.deleteDocument, "groups/" + id);
        const loggedInUserEmail = yield call(getCurrentUserEmail);
        const userQuerySnapshot = yield call(
            rsf.firestore.getCollection, db.collection('users').where('email', '==', loggedInUserEmail)
        );
        let userDetails, userId;
        if (!userQuerySnapshot.empty) {
            userDetails = userQuerySnapshot.docs[0].data();
            userId = userQuerySnapshot.docs[0].id;
        } else {
            throw new Error('User with specified email not found');
        }
        userDetails.userGroups = userDetails.userGroups.filter(x => x !== group.name);
        yield call(rsf.firestore.setDocument, 'users/' + userId, userDetails);
        saveState('user', ({ ...userDetails }));
        yield put(
            groupsActions.createDeleteGroupActionSuccess({
                groupsSuccess: '',
            })
        );
        yield put(
            groupsActions.createLoadMyGroupsActionStart()
        );
    } catch (error) {
        yield put(
            groupsActions.createDeleteGroupActionFailed({
                groupsError: error,
            })
        );
    }
}

function* clearFlags() {
    yield put(
        groupsActions.createLoadMyGroupsActionSuccess({
            groupsSuccess: '',
            groupsError: ''
        })
    );
}

export default function* groupsSaga() {
    yield all([
        takeEvery(groupsActions.CLEAR_FLAGS, clearFlags),
        takeEvery(groupsActions.CREATE_GROUP_START, createGroupSaga),
        takeEvery(groupsActions.LOAD_MY_GROUPS_START, loadMyGroupsSaga),
        takeEvery(groupsActions.EDIT_GROUP_START, editGroupSaga),
        takeEvery(groupsActions.DELETE_GROUP_START, deleteGroupSaga),
        takeEvery(groupsActions.ABANDON_GROUP_START, abandonGroupSaga),
        takeEvery(groupsActions.UNASSIGN_MEMBER_START, unassignMembersSaga),
        takeEvery(groupsActions.INVITE_MEMBER_START, inviteMemberSaga)
    ]);
}
